import api from "@/helpers/api";

const API_BASE = `/api/connection-apps`;

export default {
    namespaced: true,
    state: {
        status: '',
        connections: [],
        branches: [],
        currentConnection: null,
        errMessage: '',
        meta: {
            limit: 12,
            page: 1,
            total: 1
        },
        filter: {},
        search: {}
    },
    mutations: {
        request(state) {
            state.status = 'loading'
        },
        success(state) {
            state.status = 'success'
        },
        error(state, { message }) {
            state.status = 'error'
            state.errMessage = message
        },
        set_connections(state, { connections, meta }) {
            state.connections = [...connections]
            if (meta) {
                state.meta = meta
            }
        },
        set_branches(state, { branches }) {
            state.branches = [...branches]
        },
        set_current_connection(state, { connection }) {
            state.currentConnection = connection
        },
        switch_page(state, { meta }) {
            state.meta = { ...meta }
        },
        set_search(state, { search }) {
            state.search = { ...search }
        },
        clear_search(state) {
            state.search = {}
        },
        reset_meta(state) {
            state.meta = {
                limit: 10,
                page: 1,
                total: 1,
            }
        }
    },
    actions: {
        // Get list of all connections
        read({ commit, state }) {
            commit('request');
            const { meta, search } = state;

            return new Promise((resolve, reject) => {
                api.request('get', API_BASE, { meta, search })
                    .then((result) => {
                        const responseData = result.data.data || {};
                        const connections = responseData.data_res || [];
                        const meta = responseData.meta || {
                            page: state.meta.page,
                            limit: state.meta.limit,
                            total: connections.length
                        }

                        commit('set_connections', { connections, meta });
                        commit('success');
                        return resolve(result);
                    }).catch((err) => {
                        commit('error', { message: err });
                        return reject(err);
                    });
            });
        },

        // Get available branches for setup
        getAvailableBranches({ commit }) {
            commit('request');

            return new Promise((resolve, reject) => {
                api.request('get', `${API_BASE}/available-branches`)
                    .then((result) => {
                        commit('set_branches', { branches: result.data.data || [] });
                        commit('success');
                        return resolve(result);
                    }).catch((err) => {
                        commit('error', { message: err });
                        return reject(err);
                    });
            });
        },

        // Get single connection by ID
        read_single({ commit }, id) {
            commit('request');

            return new Promise((resolve, reject) => {
                api.request('get', `${API_BASE}/${id}`)
                    .then((result) => {
                        commit('set_current_connection', { connection: result.data.data });
                        commit('success');
                        return resolve(result.data.data);
                    }).catch((err) => {
                        commit('error', { message: err });
                        return reject(err);
                    });
            });
        },

        // Setup Step 1: Create/select branch and merchant
        setupStep1({ commit }, { data }) {
            commit('request');

            return new Promise((resolve, reject) => {
                api.request('post', `${API_BASE}/setup/step-1`, {}, data)
                    .then((request) => {
                        commit('success');
                        return resolve(request);
                    }).catch((err) => {
                        commit('error', { message: err });
                        return reject(err);
                    });
            });
        },

        // Setup Step 2: Create connection with property_id and token_name
        setupStep2({ commit }, { data }) {
            commit('request');

            return new Promise((resolve, reject) => {
                api.request('post', `${API_BASE}/setup/step-2`, {}, data)
                    .then((request) => {
                        commit('success');
                        return resolve(request);
                    }).catch((err) => {
                        commit('error', { message: err });
                        return reject(err);
                    });
            });
        },

        // Get credentials for a connection
        getCredentials({ commit }, id) {
            commit('request');

            return new Promise((resolve, reject) => {
                api.request('get', `${API_BASE}/${id}/credentials`)
                    .then((result) => {
                        commit('success');
                        return resolve(result.data.data);
                    }).catch((err) => {
                        commit('error', { message: err });
                        return reject(err);
                    });
            });
        },

        // Update connection
        update({ commit }, { id, data }) {
            commit('request');

            return new Promise((resolve, reject) => {
                api.request('put', `${API_BASE}/${id}`, {}, data)
                    .then((request) => {
                        commit('success');
                        return resolve(request);
                    }).catch((err) => {
                        commit('error', { message: err });
                        return reject(err);
                    });
            });
        },

        // Regenerate token
        regenerateToken({ commit }, { id, tokenName }) {
            commit('request');

            return new Promise((resolve, reject) => {
                api.request('post', `${API_BASE}/${id}/regenerate-token`, {}, { token_name: tokenName })
                    .then((request) => {
                        commit('success');
                        return resolve(request);
                    }).catch((err) => {
                        commit('error', { message: err });
                        return reject(err);
                    });
            });
        },

        // Update token authority role
        updateServiceAccountRole({ commit }, { id, roleName }) {
            commit('request');
            return new Promise((resolve, reject) => {
                api.request('put', `${API_BASE}/${id}/service-account-role`, {}, { role_name: roleName })
                    .then((request) => {
                        commit('success');
                        return resolve(request);
                    }).catch((err) => {
                        commit('error', { message: err });
                        return reject(err);
                    });
            });
        },

        // Delete connection
        delete({ commit, dispatch }, id) {
            commit('request');

            return new Promise((resolve, reject) => {
                api.request('delete', `${API_BASE}/${id}`)
                    .then((response) => {
                        dispatch('read'); // Reload data
                        commit('success');
                        return resolve(response.data);
                    }).catch((err) => {
                        commit('error', { message: err });
                        return reject(err);
                    });
            });
        }
    },
    getters: {
        connections: (state) => state.connections,
        branches: (state) => state.branches,
        currentConnection: (state) => state.currentConnection,
        status: (state) => state.status,
        errMessage: (state) => state.errMessage
    }
}
